import { formatCurrency } from '@hue/design-system';

/// Como o valor de um campo da trilha é dito na tela.
///
/// O que o servidor grava é o dado cru — `45000`, `2026-08-15`, `TO_REIMBURSE`, `null` — porque é
/// isso que permite comparar duas versões sem ambiguidade. Aqui ele vira o que a pessoa reconhece,
/// senão a auditoria fica ilegível justamente para quem precisa dela.
const ISO_DATE = /^\d{4}-\d{2}-\d{2}(?:T|$)/u;
const ENUM_CASE = /^[A-Z][A-Z_]+$/u;

/// Situações que o nome capitalizado não explica ("Waiting", "Paid after close"). Por tela, porque
/// o mesmo `CANCELED` é "Cancelada" na cobrança e "Cancelado" no pagamento.
const statusLabels: Record<string, Record<string, string>> = {
  FINANCE_MOVEMENT: {
    ACTIVE: 'Ativo',
    CANCELED: 'Cancelado',
    WITHDRAWN: 'Retirado pela importação',
  },
  ONLINE_CHARGE: {
    CREATING: 'Sendo criada no provedor',
    WAITING: 'Aguardando pagamento',
    PAID: 'Paga',
    CANCELED: 'Cancelada',
    EXPIRED: 'Vencida',
    FAILED: 'Recusada pelo provedor',
    REFUNDED: 'Estornada',
    PAID_AFTER_CLOSE: 'Paga depois de encerrada',
  },
  SALE_PAYMENT: {
    PENDING: 'Aguardando pagamento',
    AUTHORIZED: 'Autorizado',
    CONFIRMED: 'Confirmado',
    REVERSED: 'Estornado',
    CANCELED: 'Cancelado',
  },
};

export function formatAuditValue(field: string, value: unknown, entityType?: string): string {
  if (value === null || value === undefined || value === '') return 'vazio';
  if (typeof value === 'boolean') return value ? 'sim' : 'não';
  if (Array.isArray(value)) {
    return value.length === 0
      ? 'vazio'
      : value.map((item) => formatAuditValue(field, item, entityType)).join(', ');
  }
  if (field === 'status' && typeof value === 'string' && entityType) {
    const label = statusLabels[entityType]?.[value];
    if (label) return label;
  }
  if (typeof value === 'number') {
    return /cents$/iu.test(field) ? formatCurrency(value) : String(value);
  }
  if (typeof value !== 'string') return JSON.stringify(value);
  if (ISO_DATE.test(value)) return formatCivil(value);
  // Enums do servidor (TO_REIMBURSE, BANK_SLIP) não são para leitura humana.
  if (ENUM_CASE.test(value)) {
    return value
      .toLowerCase()
      .replace(/_/gu, ' ')
      .replace(/^./u, (letter) => letter.toUpperCase());
  }
  return value;
}

/// A data já vem como dia civil (`2026-08-15`), sem hora e sem fuso: virar `Date` para formatar
/// abriria a porta para o dia mudar por causa do fuso do navegador.
function formatCivil(value: string): string {
  const [year, month, day] = value.slice(0, 10).split('-');
  return `${day}/${month}/${year}`;
}

export type AuditTone = 'positive' | 'progress' | 'danger' | 'neutral';

/// A cor do ponto ao lado do evento, tirada do próprio nome da ação: criar é verde, editar é azul,
/// excluir e recusar são vermelhos. Sem isso, uma exclusão parece igual a um login numa lista de
/// cinquenta linhas.
export function auditTone(action: string): AuditTone {
  if (/(?:deleted|removed|deactivated|failed)$/u.test(action)) return 'danger';
  if (/(?:created|invited|activated|saved|attached|bootstrapped)$/u.test(action)) return 'positive';
  if (/(?:updated|repaid|settled|cleared|reissued|selected|entered|left)$/u.test(action)) {
    return 'progress';
  }
  return 'neutral';
}
